// Owned by the workbench: AIOE mirrors these shapes (mju-aioe/packages/shared/src/schemas/policyExceptions.ts). The workbench never mirrors it back. Keep the two identical.
/**
 * Ask for an exception (D379). The idea of a permission request beside a refusal is AgentField's (Agent-Field/agentfield,
 * Apache-2.0; ideas only, no code), as docs/research/2026-10-agentfield-swe-af-audit.md section 6 sets out.
 *
 * When the policy refuses something — a model below the project's classification, a tool server, a network address, a
 * runtime — the person sees the refusal where it happened, with **Ask for an exception**: what, for which project, why,
 * and for how long. The request goes to the organisation, which lists it for its administrators (the console, their Inbox
 * and phone); an approval writes a scoped, time-limited exception into the policy document the machine already pulls
 * (`GET /policy/v1?projectId=`, src/platform/policyPull.ts, `.mojoup/policy.json` `exceptions`). The machine honours it
 * until it expires and then on its own stops; every step is audited on both sides. Without an organisation (Free, or
 * Teams without one) there is no one to ask, and the button is not shown.
 *
 * Routes (the organisation; bearer: the person's sign-in on the desktop, the device's on a node naming the person with
 * `x-aioe-principal`):
 *
 *   POST /policy/v1/exception-requests            {@link ExceptionRequestInput} → 201 {@link ExceptionRequestRecord}
 *                                                  200 with the open one when the same person already asked for the
 *                                                  same thing in the same project and it is still pending;
 *                                                  403 `not_member` (not in the project), `exceptions_off` (the
 *                                                  organisation takes no requests); 400 `invalid` with the problem.
 *   GET  /policy/v1/exception-requests?mine=1     → { requests: {@link ExceptionRequestRecord}[] } — the caller's own,
 *        [&projectId=prj_…]                         newest first, at most 100; an answered one keeps its `answer`.
 *   POST /policy/v1/exception-requests/:id/withdraw → {@link ExceptionRequestRecord} (state `withdrawn`): the person who
 *                                                  asked, while it is pending.
 *
 * The policy document gains `exceptions` ({@link policyExceptionSchema}[]): one entry per approved request, scoped to the
 * project the document is for and to what was asked, each with `until`. The organisation stops listing an entry once it
 * expires or is revoked; the machine stops honouring it at `until` whatever the document says.
 *
 * An approval or a decline is also an Inbox record for the person who asked, filed by the organisation with the dedupe
 * key {@link exceptionAnswerDedupeKey} (the machine files the same key when it reads the answer, so there is one record).
 * A new request is an Inbox record for the project's administrators: kind `decision`, needs-you, actions Approve (rpc
 * `aioe.policy.exception.decide`, `decision: "approve"`, `hours`) and Decline (`decision: "decline"`, a reason).
 *
 * Words for people: "Ask for an exception", "for how long", "why", "approved until …", "declined". Never "subject",
 * "fingerprint" or "scope" on screen.
 *
 * Pure: zod and string helpers only.
 */
import { z } from "zod";
// ── What is asked for ──────────────────────────────────────────────────
export const POLICY_EXCEPTION_KINDS = ["model", "runtime", "tool-server", "network"];
const hex64 = z.string().regex(/^[0-9a-f]{64}$/i);
/** What a refusal was about, and so what an exception allows. */
export const policyExceptionSubjectSchema = z.discriminatedUnion("kind", [
    /** A model provider (catalogue id: `openai`, `anthropic`, `claude-code` …) and, optionally, one model of it. */
    z.object({ kind: z.literal("model").describe("What the exception allows: a model provider or one of its models."), providerId: z.string().min(1).max(100).describe("The model provider (catalogue id) the exception allows."), model: z.string().min(1).max(200).optional().describe("One model of that provider; absent, any of its models.") }),
    /** An agent runtime (`codex`, `claude-code`, `gemini` …). */
    z.object({ kind: z.literal("runtime").describe("What the exception allows: an agent runtime."), runtime: z.string().min(1).max(100).describe("The agent runtime the exception allows.") }),
    /** A tool server, by the fingerprint the workbench computes (shared/schemas/tools.ts), with its name for the words. */
    z.object({ kind: z.literal("tool-server").describe("What the exception allows: a tool server."), server: z.string().min(1).max(200).describe("The tool server's name, as the repository or person configured it."), fingerprint: hex64.describe("The server's fingerprint (SHA-256 of its definition): what the exception allows.") }),
    /** A network address: a host, or `*.example.com`, and optionally one port. */
    z.object({ kind: z.literal("network").describe("What the exception allows: a network address."), host: z.string().min(1).max(253).describe("The host agents may connect to (exact, or *.example.com)."), port: z.number().int().min(1).max(65535).optional().describe("One port; absent, any port.") }),
]);
/** The lengths the person picks from, in hours. */
export const EXCEPTION_DURATIONS = [
    { hours: 8, words: "For today" },
    { hours: 24, words: "For a day" },
    { hours: 7 * 24, words: "For a week" },
    { hours: 30 * 24, words: "For 30 days" },
];
/** The longest an exception may be asked for: 90 days. */
export const MAX_EXCEPTION_HOURS = 90 * 24;
// ── The request and its answer ─────────────────────────────────────────
/** `POST /policy/v1/exception-requests`. */
export const exceptionRequestInputSchema = z.object({
    /** The organisation's project (`prj_…`) the refusal happened in. */
    projectId: z.string().min(1).max(100),
    subject: policyExceptionSubjectSchema,
    /** Why the person needs it, in their words. */
    why: z.string().trim().min(1).max(2000),
    /** For how long, in hours. */
    hours: z.number().int().min(1).max(MAX_EXCEPTION_HOURS),
    /** The refusal as the person read it, for the administrator. */
    refusal: z.string().max(500).optional(),
    /** Where it happened, in words: the repository and the machine. */
    where: z.object({ repository: z.string().max(200).optional(), machine: z.string().max(200).optional() }).optional(),
});
export const EXCEPTION_REQUEST_STATES = ["pending", "approved", "declined", "withdrawn", "expired"];
/** A request as the organisation keeps it and answers it. */
export const exceptionRequestRecordSchema = exceptionRequestInputSchema.extend({
    id: z.string().min(1).max(100),
    state: z.enum(EXCEPTION_REQUEST_STATES),
    askedAt: z.string().max(64),
    /** Who asked: their principal and name. */
    askedBy: z.object({ principal: z.string().max(200).optional(), name: z.string().max(200).optional() }).optional(),
    /** Set once it is answered. `until` on an approval: when the exception ends (it may be shorter than asked). */
    answer: z
        .object({
        by: z.object({ principal: z.string().max(200).optional(), name: z.string().max(200).optional() }).optional(),
        at: z.string().max(64),
        note: z.string().max(2000).optional(),
        until: z.string().max(64).optional(),
    })
        .optional(),
});
// ── In the policy document ─────────────────────────────────────────────
/** One approved exception in the policy document's `exceptions` (`.mojoup/policy.json`). */
export const policyExceptionSchema = z.object({
    id: z.string().min(1).max(100).describe("The exception's id at the organisation."),
    requestId: z.string().max(100).optional().describe("The request it answers."),
    projectId: z.string().max(100).optional().describe("The organisation's project it applies to; absent, the project the document is for."),
    subject: policyExceptionSubjectSchema.describe("What it allows: a model, a runtime, a tool server or a network address."),
    until: z.string().min(1).max(64).describe("When it ends (ISO 8601). The machine stops honouring it then, whatever the document says."),
    grantedBy: z.string().max(200).optional().describe("Who approved it, in words."),
    why: z.string().max(2000).optional().describe("Why it was asked for, as the person wrote it."),
});
/** What the person sends from the window or the Inbox: the project is the host's, never the caller's. */
export const exceptionAskSchema = z.object({
    subject: policyExceptionSubjectSchema,
    why: z.string().trim().min(1, "say why you need it").max(2000),
    hours: z.number().int().min(1).max(MAX_EXCEPTION_HOURS),
    refusal: z.string().max(500).optional(),
});
/** The Control API method the Inbox's and the window's "Ask for an exception" call. */
export const ASK_EXCEPTION_RPC = "policy.exceptions.request";
/** The Inbox record that carries an answer, the same on both sides. */
export function exceptionAnswerDedupeKey(requestId) {
    return `policy-exception:${requestId}`;
}
// ── Rules ──────────────────────────────────────────────────────────────
const hostMatches = (pattern, host) => {
    const p = pattern.toLowerCase();
    const h = host.toLowerCase();
    if (p.startsWith("*."))
        return h.endsWith(p.slice(1)) && h.length > p.length - 1;
    return p === h;
};
/** Whether an exception's subject allows what was refused. */
export function subjectCovers(granted, asked) {
    switch (granted.kind) {
        case "model":
            return asked.kind === "model" && asked.providerId === granted.providerId && (!granted.model || granted.model === asked.model);
        case "runtime":
            return asked.kind === "runtime" && asked.runtime === granted.runtime;
        case "tool-server":
            return asked.kind === "tool-server" && asked.fingerprint.toLowerCase() === granted.fingerprint.toLowerCase();
        case "network":
            return asked.kind === "network" && hostMatches(granted.host, asked.host) && (granted.port === undefined || granted.port === asked.port);
    }
}
/** The same thing asked: what makes two requests one. */
export function sameSubject(a, b) {
    return subjectCovers(a, b) && subjectCovers(b, a);
}
/** The exceptions in force now, for this project (an entry naming another project is not this one's). */
export function liveExceptions(list, now, projectId) {
    return (list ?? []).filter((e) => {
        const until = Date.parse(e.until);
        return Number.isFinite(until) && until > now && (!e.projectId || !projectId || e.projectId === projectId);
    });
}
/** The first live exception that allows this, or undefined. */
export function exceptionFor(list, asked, now, projectId) {
    return liveExceptions(list, now, projectId).find((e) => subjectCovers(e.subject, asked));
}
// ── Words ──────────────────────────────────────────────────────────────
/** What was refused or asked for, in words: "the model gpt-4o from openai", "the Codex runtime". */
export function describeSubject(s, name = (id) => id) {
    switch (s.kind) {
        case "model":
            return s.model ? `the model ${s.model} from ${name(s.providerId)}` : `${name(s.providerId)}'s models`;
        case "runtime":
            return `the ${name(s.runtime)} runtime`;
        case "tool-server":
            return `the tool server ${s.server}`;
        case "network":
            return `connections to ${s.host}${s.port ? ` on port ${s.port}` : ""}`;
    }
}
/** How long, in words: "for a week", "for 3 days". */
export function durationWords(hours) {
    const listed = EXCEPTION_DURATIONS.find((d) => d.hours === hours);
    if (listed)
        return listed.words.charAt(0).toLowerCase() + listed.words.slice(1);
    if (hours % 24 === 0)
        return `for ${hours / 24} ${hours === 24 ? "day" : "days"}`;
    return `for ${hours} ${hours === 1 ? "hour" : "hours"}`;
}
/** Where a request stands, in one sentence the person reads where they were refused and in their Inbox. */
export function requestStateWords(r, when = (iso) => iso, name) {
    const what = describeSubject(r.subject, name);
    const by = r.answer?.by?.name ? ` by ${r.answer.by.name}` : "";
    const note = r.answer?.note ? `: ${r.answer.note}` : ".";
    switch (r.state) {
        case "pending":
            return `You asked for an exception for ${what} ${durationWords(r.hours)}. Waiting for an administrator.`;
        case "approved":
            return `Your exception for ${what} was approved${by}${r.answer?.until ? `, until ${when(r.answer.until)}` : ""}${note}`;
        case "declined":
            return `Your exception for ${what} was declined${by}${note}`;
        case "withdrawn":
            return `You withdrew your request for ${what}.`;
        case "expired":
            return `Your exception for ${what} has ended.`;
    }
}
//# sourceMappingURL=policyExceptions.js.map